<template>
    <v-text-field
        v-bind="$attrs"
        :value="text"
        :suffix="unit || undefined"
        :disabled="disabled"
        :label="label"
        :error-messages="errorMessages"
        type="text"
        inputmode="decimal"
        autocomplete="off"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown="$emit('keydown', $event)"
    />
</template>

<script>
import {
    formatDecimalForInput,
    parseDecimalInput,
} from "@/utils/numberFormat";

/**
 * Locale-aware quantity input (no unit conversion; see QuantityUnitInput
 * for stock-unit aware inputs).
 * Accepts both comma and point as decimal separator, displays the app locale
 * separator, and emits a normalized Number (or null when empty/invalid).
 */
export default {
    name: "QuantityInput",
    inheritAttrs: false,
    props: {
        value: {
            type: [Number, String],
            default: null,
        },
        allowDecimal: {
            type: Boolean,
            default: true,
        },
        maxDecimals: {
            type: Number,
            default: 3,
        },
        allowNegative: {
            type: Boolean,
            default: false,
        },
        unit: {
            type: String,
            default: "",
        },
        label: {
            type: String,
            default: "",
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        errorMessages: {
            type: [Array, String],
            default: () => [],
        },
    },
    data() {
        return {
            text: "",
            focused: false,
        };
    },
    computed: {
        effectiveMaxDecimals() {
            return this.allowDecimal ? this.maxDecimals : 0;
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(newValue) {
                if (!this.focused) {
                    this.text = formatDecimalForInput(
                        newValue,
                        this.effectiveMaxDecimals
                    );
                }
            },
        },
    },
    methods: {
        onInput(raw) {
            this.text = raw;
            this.$emit("input", this.parse(raw));
        },
        onFocus(event) {
            this.focused = true;
            this.$emit("focus", event);
        },
        onBlur(event) {
            this.focused = false;
            this.text = formatDecimalForInput(
                this.parse(this.text),
                this.effectiveMaxDecimals
            );
            this.$emit("blur", event);
        },
        parse(raw) {
            const parsed = parseDecimalInput(raw, {
                maxDecimals: this.effectiveMaxDecimals,
                allowNegative: this.allowNegative,
            });
            if (parsed === null) {
                return null;
            }
            if (!this.allowDecimal && !Number.isInteger(parsed)) {
                return Math.trunc(parsed);
            }
            return parsed;
        },
    },
};
</script>
